import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import type {useNavigate} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {CommentIcon, IssueDraftIcon, TasklistIcon} from '@primer/octicons-react'
import {useMemo} from 'react'

import {usePlugins} from '../plugin/ImmersivePluginsProvider'
import type {CopilotChatManager} from '../utils/copilot-chat-manager'
import type {CopilotChatState} from '../utils/copilot-chat-reducer'
import {type CopilotChatCommandType, type ModeSupport, ModeSupportName} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'

export interface AvailableCommand {
  key: CopilotChatCommandType
  name: string
  description: string
  icon: Icon
  isNew?: boolean
  order?: number
  suggestions: string[]
  enabled?: () => boolean
  disabledAction?: (navigate: ReturnType<typeof useNavigate>) => void
  /**
   * Execute the command action. Returns true if the command should prevent message submission.
   */
  execute?: (
    params: string,
    state: CopilotChatState,
    navigate: ReturnType<typeof useNavigate>,
    manager: CopilotChatManager,
  ) => Promise<boolean> | boolean
  mode: ModeSupport
  /**
   * Optional custom model picker to display when this slash command is active.
   */
  customModelPicker?: (props: {selectedAgent?: SelectedAgent}) => React.ReactNode
}

/**
 * Hook to get all available slash commands from plugins and built-in commands.
 */

type Options = {
  enabled: boolean
}

export function useAvailableCommands({enabled}: Options) {
  const plugins = usePlugins()

  const availableCommands = useMemo(() => {
    const commands: AvailableCommand[] = []

    if (!enabled) {
      return commands
    }

    // Add plugin commands
    for (const plugin of plugins) {
      if (plugin.slashCommand) {
        commands.push({
          key: plugin.slashCommand.key as CopilotChatCommandType,
          name: plugin.slashCommand.name,
          description: plugin.slashCommand.description,
          icon: plugin.slashCommand.icon,
          isNew: plugin.slashCommand.isNew,
          order: plugin.slashCommand.order,
          suggestions: plugin.slashCommand.suggestions,
          enabled: plugin.slashCommand.enabled,
          disabledAction: plugin.slashCommand.disabledAction,
          customModelPicker: plugin.slashCommand.customModelPicker,
          execute: async (params, state, navigate) => {
            return await plugin.slashCommand!.execute(params, state, navigate)
          },
          mode: plugin.slashCommand.mode,
        })
      }
    }

    // Add built-in commands
    if (copilotFeatureFlags.planningAgent) {
      commands.push({
        key: 'brainstorm',
        name: 'Brainstorm',
        description: 'Create a plan and turn them into tasks',
        icon: TasklistIcon,
        suggestions: [],
        execute: (params, state, navigate, manager) => {
          manager.setUsePlanningIntent(true)
          return false
        },
        mode: {
          name: ModeSupportName.Brainstorm,
          icon: TasklistIcon,
          supportsMCP: true,
          supportsAttachments: true,
          supportsModelSwitching: true,
          supportsSpaces: true,
          supportsAutocompletion: true,
        },
      })
    }

    if (copilotFeatureFlags.askModeDropdown) {
      commands.push({
        key: 'ask',
        name: 'Ask',
        description: 'Get an answer to a question',
        icon: CommentIcon,
        order: 0,
        suggestions: [],
        execute: () => {
          return false
        },
        mode: {
          name: ModeSupportName.Ask,
          icon: CommentIcon,
          supportsMCP: true,
          supportsAttachments: true,
          supportsModelSwitching: true,
          supportsSpaces: true,
          supportsAutocompletion: true,
        },
      })
    }

    commands.push({
      key: 'create-issue',
      name: 'Create issue',
      description: 'Create a GitHub issue from your conversation',
      icon: IssueDraftIcon,
      suggestions: [
        'First, create a new draft issue. Then ask for additional information to fill out the issue.',
        'First, create an issue with sub issues. Then ask for additional information to fill out these issues.',
      ],
      execute: () => {
        return false
      },
      mode: {
        name: ModeSupportName.CreateIssue,
        icon: IssueDraftIcon,
        supportsMCP: true,
        supportsAttachments: true,
        supportsModelSwitching: true,
        supportsSpaces: true,
        supportsAutocompletion: true,
      },
    })

    // sort commands: by order (if exists), then alphabetically by name
    return commands.sort((a, b) => {
      // If both have order, sort by order
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order
      }
      // If only a has order, it comes first
      if (a.order !== undefined) {
        return -1
      }
      // If only b has order, it comes first
      if (b.order !== undefined) {
        return 1
      }
      // Neither has order, sort alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [plugins, enabled])

  return {availableCommands, availableCommandKeys: availableCommands.map(cmd => cmd.key)} as const
}
